/**
 * Programa de referidos — utilidades client-safe (sin Node `crypto`;
 * la generación vive en `referrals-server.ts` por el mismo motivo que
 * `promo-codes-server.ts`: evitar el polyfill en el bundle + CSP).
 *
 * Research 2026 (cita obligatoria + CORRECCIÓN DEL DUEÑO 2026-09-23):
 * - Track360 "How to Build a SaaS Referral Program (2026 Playbook)"
 *   (track360.io/blog/saas-referral-program-build-guide-2026): doble
 *   recompensa por defecto (referrer + referido), crédito de cuenta
 *   antes que cash, link único por usuario, hub con link/estado, y
 *   nunca recompensar antes del evento verificado.
 * - Cello.so "Referral Programs for SaaS: Build Guide June 2026"
 *   (cello.so/what-is-referral-program-build-saas, verificado 2026-09-23
 *   vía TinyFish): el referrer cobra cuando la primera factura del
 *   invitado se liquida — sin suscripción no hay premio (patrón OpenCode
 *   que adopta esta corrección: registro ≠ recompensa).
 * - Voucherify "Double-sided referrals" (2026): el doble-sided atrae
 *   auto-referidos → validación estricta (formato + UNIQUE + CHECK).
 * - Extole "25 Personalized Referral Incentive Ideas" (2026-04-03):
 *   el referido debe ser un usuario nuevo genuino.
 *
 * Diseño con lo EXISTENTE (sin facturación paralela): la recompensa
 * es un entitlement temporal de Pro vía `mergePromoCodeMetadata`
 * (mismas reglas de apilado que los promo codes) — PLAN_CONFIG y
 * billing-plans intactos como SSOT. Ver docs/referrals-2026.md.
 *
 * CORRECCIÓN 2026-09-23 (manda sobre fase 2): el registro con el link
 * NO otorga nada a NADIE. La doble recompensa (14d Pro a AMBOS) se
 * acredita solo cuando el invitado confirma suscripción paga
 * (webhook Wompi APPROVED → `grantReferralRewardsOnSubscription` en
 * `src/lib/referrals-subscription.ts`, llamado desde
 * `apply-approved-plan.ts` — esa señal es la única fuente de verdad,
 * no se crea otra). Sin pago no hay premio, como hace OpenCode.
 */

/** Plan otorgado por referido — SSOT display vía planNameKey/getPromoCodeDisplayPlan. */
export const REFERRAL_REWARD_PLAN = "pro" as const;
/**
 * Días de Pro por lado (referrer + referido). Economía Track360 paso 1:
 * Pro $8/mes (SSOT billing-plans) → 14d ≈ $3.73 de valor nominal,
 * costo marginal ~0 (quotas), muy por debajo del CAC pagado.
 */
export const REFERRAL_REWARD_DAYS = 14;

export const REFERRAL_CODE_LENGTH = 8;
/** Charset sin ambiguos (0/O, 1/I/L fuera) — paridad promo SAFE_CHARSET. */
export const REFERRAL_CODE_CHARSET = "ABCDEFGHJKMNPQRSTUVWXYZ23456789";

/** Query param del link único: /signup?ref=XXXXXXXX. */
export const REFERRAL_QUERY_PARAM = "ref";
/** localStorage: código pendiente hasta reclamarlo autenticado. */
export const REFERRAL_STORAGE_KEY = "vortixy:referral:pending";

/** Estados de un referido (CORRECCIÓN 2026-09-23: `pending` = link
 * vinculado, a la espera de que el invitado se suscriba — NADIE ha
 * cobrado todavía; `rewarded` = doble recompensa acreditada al
 * confirmarse la suscripción paga. `invitee_rewarded` es LEGADO
 * (filas pre-corrección donde el invitado ya cobró con la regla
 * anterior + intermedio crash-safe del grant por suscripción: tras
 * otorgar al invitado y antes de otorgar al referrer — nunca lo crea
 * el claim). Ver migración 20260925000000_referrals_fase2.sql (CHECK
 * intacto, sin migración nueva) + docs/referrals-2026.md). */
export type ReferralStatus = "pending" | "invitee_rewarded" | "rewarded";

/**
 * Valida el formato de un código de referido (8 chars del charset sin
 * ambiguos: A-Z menos I/L/O + 2-9 — el regex replica REFERRAL_CODE_CHARSET
 * para no aceptar lo que el generador nunca emite).
 */
export function isValidReferralCodeFormat(code: string): boolean {
  const normalized = code.trim().toUpperCase();
  if (normalized.length !== REFERRAL_CODE_LENGTH) return false;
  return /^[ABCDEFGHJKMNPQRSTUVWXYZ23456789]+$/.test(normalized);
}

/** Normaliza un código para comparar/almacenar (uppercase + trim). */
export function normalizeReferralCode(code: string): string {
  return code.trim().toUpperCase();
}

/** Link único de invitación: {origin}/signup?ref=CODE. */
export function buildReferralSignupLink(origin: string, code: string): string {
  const clean = origin.replace(/\/$/, "");
  return `${clean}/signup?${REFERRAL_QUERY_PARAM}=${encodeURIComponent(normalizeReferralCode(code))}`;
}

/**
 * CORRECCIÓN 2026-09-23 anti-fraude vigente (mantiene Fase 2 menos el
 * gate de genuinidad, sustituido por "suscripción paga" — ver
 * docs/referrals-2026.md § Corrección del dueño).
 *
 * Research 2026 (cita obligatoria TAREA FASE 2 + corrección):
 * - Stripe "Free trial abuse: Patterns, signals, and prevention tactics for
 *   SaaS platforms" (2026-07-24): el abuso de trials/referidos comparte
 *   señales — emails desechables (blocklist de dominios conocidos),
 *   velocidad de signups por IP/subnet y patrones de email.
 * - Extole "Security and fraud prevention in referral programs" (2026-07-06):
 *   modelo por niveles (anónimo → identificado → verificado) y
 *   "event-based reward triggers": recompensar solo tras un evento
 *   calificador downstream (aquí: primera factura pagada — Cello.so
 *   junio 2026), no ante el referido en sí.
 * - Fingerprint "How to protect your business from referral fraud"
 *   (2026-09-21): pedir verificación desalienta granjas; aquí el gate
 *   de pago es aún más fuerte (una granja no paga suscripciones).
 * - RefGrow "How to Prevent Affiliate Fraud" (2026): velocity checks por
 *   afiliado (conversiones/día muy por encima de lo normal = granja) +
 *   bloqueo de emails desechables.
 *
 * Techos documentados (scoring mínimo anti-granja, best-effort + RPC
 * atómico como verdad para el insert):
 * - IP: REFERRAL_MAX_CLAIMS_PER_IP_PER_DAY claims/día (intentos válidos
 *   autenticados; los éxitos son un subconjunto — fail-closed).
 * - Referrer: REFERRAL_MAX_REFERRALS_PER_USER_PER_MONTH referidos/mes
 *   natural (20 × 14d Pro ≈ $46 nominales de tope de exposición mensual
 *   por cuenta; un sharer legítimo rara vez supera 20/mes — RefGrow
 *   velocity: picos muy por encima de 2-3/mes se verifican antes de pagar).
 */

/** Tope de claims de referido por IP y día (fail-closed, ver claim/route.ts). */
export const REFERRAL_MAX_CLAIMS_PER_IP_PER_DAY = 10;
/** Tope de referidos acreditables por usuario y mes natural (fail-closed). */
export const REFERRAL_MAX_REFERRALS_PER_USER_PER_MONTH = 20;

/**
 * CORRECCIÓN 2026-09-23: el antiguo gate de genuinidad (email verificado
 * O `funnel_first_prompt`) queda ELIMINADO — la única señal que libera la
 * recompensa es la suscripción paga del invitado (webhook APPROVED →
 * `grantReferralRewardsOnSubscription`). La constante se retira; si algún
 * import residual la referencia, el typecheck lo señalará para limpiarlo.
 */

/**
 * Dominios de email desechable/temporal conocidos (curaduría mínima, sin
 * dependencia nueva — research 2026 Stripe/RequestGuard: blocklist de
 * dominios conocidos + patrones; el matching es exacto o por subdominio).
 * Lista intencionalmente corta y sin ambigüedades (nada de ymail/gmail/
 * outlook y similares legítimos).
 */
export const REFERRAL_DISPOSABLE_DOMAINS: readonly string[] = [
  "10minutemail.com",
  "10minutemail.net",
  "20minutemail.com",
  "dispostable.com",
  "emailondeck.com",
  "fakemail.net",
  "fakeinbox.com",
  "getnada.com",
  "guerrillamail.com",
  "guerrillamail.net",
  "guerrillamail.org",
  "harakirimail.com",
  "incognitomail.com",
  "jetable.org",
  "maildrop.cc",
  "mailinator.com",
  "mailnesia.com",
  "mintemail.com",
  "moakt.com",
  "mohmal.com",
  "mytemp.email",
  "sharklasers.com",
  "temp-mail.io",
  "temp-mail.org",
  "tempinbox.com",
  "tempmail.com",
  "tempmail.org",
  "throwawaymail.com",
  "trash-mail.com",
  "trashmail.com",
  "yopmail.com",
  "yopmail.fr",
  "yopmail.net",
];

/** Extrae el dominio de un email (lowercase, sin espacios; "" si inválido). */
export function getReferralEmailDomain(email: string): string {
  const at = email.trim().toLowerCase().lastIndexOf("@");
  if (at <= 0) return "";
  const domain = email
    .trim()
    .toLowerCase()
    .slice(at + 1);
  if (!domain || domain.includes(" ") || !domain.includes(".")) return "";
  return domain;
}

/**
 * ¿Email desechable/temporal? Matching exacto o por subdominio
 * (`x.mailinator.com` también cae) — paridad con el consejo Stripe 2026
 * (blocklist de dominios conocidos en el claim, sin servicio externo).
 */
export function isDisposableReferralEmail(email: string): boolean {
  const domain = getReferralEmailDomain(email);
  if (!domain) return false;
  return REFERRAL_DISPOSABLE_DOMAINS.some(
    (d) => domain === d || domain.endsWith(`.${d}`),
  );
}

/** Inicio del mes natural (UTC) en ISO — ventana del tope mensual. */
export function getReferralMonthStart(now: Date = new Date()): string {
  return new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1, 0, 0, 0, 0),
  ).toISOString();
}
